import { Link } from 'react-router-dom';

// Icons as inline SVGs
const UsersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const MapIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 7m0 13V7"
    />
  </svg>
);

const LocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LibraryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

export const Home = (): JSX.Element => {
  const generators = [
    {
      path: '/npc-generator',
      title: 'NPC Generator',
      description:
        'Generate scoundrel characters with playbooks, vices, and abilities for your crew to encounter.',
      icon: <UsersIcon />,
      gradient: 'from-blue-500/20 to-purple-500/20',
      borderColor: 'group-hover:border-blue-500/50',
    },
    {
      path: '/mission-generator',
      title: 'Mission Generator',
      description:
        'Create heists and scores with targets, complications, and rewards for your crew to pursue.',
      icon: <MapIcon />,
      gradient: 'from-red-500/20 to-orange-500/20',
      borderColor: 'group-hover:border-red-500/50',
    },
    {
      path: '/location-generator',
      title: 'Location Generator',
      description:
        'Generate locations in Doskvol with districts and factions to populate your game world.',
      icon: <LocationIcon />,
      gradient: 'from-emerald-500/20 to-teal-500/20',
      borderColor: 'group-hover:border-emerald-500/50',
    },
    {
      path: '/content-library',
      title: 'Content Library',
      description:
        'Save and organize your generated content for easy access during your game sessions.',
      icon: <LibraryIcon />,
      gradient: 'from-amber-500/20 to-yellow-500/20',
      borderColor: 'group-hover:border-amber-500/50',
    },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      {/* Welcome Section */}
      <div style={{ marginBottom: '48px' }}>
        <h1
          style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#d4af37',
            marginBottom: '12px',
            fontFamily: 'Cinzel, serif',
          }}
        >
          Welcome to Doskvol
        </h1>
        <p
          style={{
            fontSize: '1.125rem',
            color: '#9ca3af',
            maxWidth: '600px',
            lineHeight: 1.6,
          }}
        >
          Generate NPCs, missions, and locations for your Blades in the Dark game. A tool for game
          masters to quickly populate the haunted industrial city.
        </p>
      </div>

      {/* Generator Cards Grid */}
      <div style={{ marginBottom: '48px' }}>
        <h2
          style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#d4af37',
            marginBottom: '24px',
            fontFamily: 'Cinzel, serif',
          }}
        >
          Quick Access
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px',
          }}
        >
          {generators.map(generator => (
            <Link
              key={generator.path}
              to={generator.path}
              className="group"
              style={{
                display: 'block',
                backgroundColor: '#16162a',
                border: '1px solid rgba(212, 175, 55, 0.2)',
                borderRadius: '12px',
                padding: '24px',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Gradient Background on Hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: `linear-gradient(135deg, ${generator.gradient.includes('blue') ? 'rgba(59, 130, 246, 0.1)' : generator.gradient.includes('red') ? 'rgba(239, 68, 68, 0.1)' : generator.gradient.includes('emerald') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)'} 0%, transparent 100%)`,
                }}
              />

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Icon */}
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#d4af37',
                    marginBottom: '16px',
                    transition: 'all 0.3s ease',
                  }}
                  className="group-hover:bg-[#d4af37] group-hover:text-[#0f0f1e]"
                >
                  {generator.icon}
                </div>

                {/* Title */}
                <h3
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#d4af37',
                    marginBottom: '8px',
                    fontFamily: 'Cinzel, serif',
                  }}
                  className="group-hover:text-[#e5c158]"
                >
                  {generator.title}
                </h3>

                {/* Description */}
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: '#9ca3af',
                    lineHeight: 1.6,
                    marginBottom: '16px',
                  }}
                >
                  {generator.description}
                </p>

                {/* CTA */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#d4af37',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                  className="group-hover:gap-3 transition-all"
                >
                  <span>Open Generator</span>
                  <span
                    style={{ transition: 'transform 0.3s ease' }}
                    className="group-hover:translate-x-1"
                  >
                    <ArrowRightIcon />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* About Section */}
      <div
        style={{
          backgroundColor: '#16162a',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          borderRadius: '12px',
          padding: '32px',
        }}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#d4af37',
            marginBottom: '16px',
            fontFamily: 'Cinzel, serif',
          }}
        >
          About Blades in the Dark
        </h2>
        <p
          style={{
            fontSize: '1rem',
            color: '#9ca3af',
            lineHeight: 1.7,
            marginBottom: '24px',
          }}
        >
          Blades in the Dark is a tabletop RPG about a crew of scoundrels seeking their fortunes in
          the haunted industrial city of Doskvol. This tool helps game masters quickly generate
          NPCs, missions, and locations to populate their game world with rich, atmospheric content.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {['Tabletop RPG', 'Content Generation', 'Doskvol', 'Scoundrels'].map(tag => (
            <span
              key={tag}
              style={{
                padding: '6px 14px',
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '20px',
                fontSize: '0.875rem',
                color: '#d4af37',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
